import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { DevTool } from "@hookform/devtools";
import ReactTooltip from "react-tooltip";
import Select from "react-dropdown-select";
import { useEventListener } from "../../_hooks";

import { ChronicleActions, WorldActions } from "../../_actions";
import { useModal } from "../../_contexts";
import {
  Button,
  Tabs,
  Icon,
  ImagePicker,
  MapPicker,
  Checkbox,
  NumberInput,
  CalendarSelect,
  Toggle,
  CalendarDatePicker,
  SubGroupPicker,
  PaneLoader,
} from "../../_components";

export function ChronicleSettingsForm({ chronicle, world, prevForm }) {
  const tooltipIcon = "fas fa-question-circle fa-fw";
  // const { subgroups } = useSelector((state) => state.world);
  const [currentMainTab, setCurrentMainTab] = useState(0);
  const [isLoading, setLoading] = useState(false);

  const { closeModal } = useModal();
  const dispatch = useDispatch();

  ReactTooltip.rebuild();

  const componentRef = useRef();

  // Determine Default Cover
  const defaultCoverID = chronicle.defaultCover
    ? typeof chronicle.defaultCover === "string"
      ? chronicle.defaultCover
      : chronicle.defaultCover?._id || ""
    : "";

  const defaultMapID = chronicle.defaultMap
    ? typeof chronicle.defaultMap === "string"
      ? chronicle.defaultMap
      : chronicle.defaultMap?._id || ""
    : "";

  const defaultCalendarID = chronicle.defaultCalendar
    ? typeof chronicle.defaultCalendar === "string"
      ? chronicle.defaultCalendar
      : chronicle.defaultCalendar?._id || ""
    : "";

  const { defaultCalendar } = chronicle;
  const granularityOptions = [];
  const units = Object.keys(defaultCalendar.time);

  for (let unit of units) {
    if (unit !== "month") {
      granularityOptions.push({
        name: defaultCalendar.time[unit].plural,
        value: unit,
      });
    }
  }

  const defaultValues = {
    //General Settings
    title: chronicle.title,
    description: chronicle.description,
    private: chronicle.private,
    subGroups: chronicle.subGroups,
    //Display Settings
    cover: defaultCoverID,
    map: defaultMapID,
    calendar: defaultCalendarID,
    options: {
      autoOpenSidebar:
        chronicle?.options?.autoOpenSidebar !== undefined
          ? chronicle.options.autoOpenSidebar
          : true,
      enableChronicleSidebar:
        chronicle?.options?.enableChronicleSidebar !== undefined
          ? chronicle.options.enableChronicleSidebar
          : true,
      sendViewersToWorld:
        chronicle?.options?.sendViewersToWorld !== undefined
          ? chronicle.options.sendViewersToWorld
          : true,
      sendAuthorsToDashboard:
        chronicle?.options?.sendAuthorsToDashboard !== undefined
          ? chronicle.options.sendAuthorsToDashboard
          : true,
      customClass:
        chronicle?.options?.customClass !== undefined
          ? chronicle.options.customClass
          : "",
    },
    granularity: chronicle.defaultUnit,
    unitCount: chronicle.unitDisplayCount,
    startDate: chronicle.startDate,
    //Time & Date Settings
    //Advanced Settings
  };
  // console.log('Chronicle Default Map', defaultValues);
  const initialDefaultValues =
    prevForm && prevForm.data
      ? { ...defaultValues, ...prevForm.data }
      : defaultValues;

  const {
    handleSubmit,
    control,
    errors,
    watch,
    getValues,
    trigger,
    formState,
  } = useForm({
    mode: "all",
    shouldUnregister: false,
    defaultValues: initialDefaultValues,
  });

  const formatClassname = (str) => {
    if (str && str.length > 0) {
      let className = str.replace(/\s/gim, "-");
      className = className.replace(
        /[\[\]\(\)\{\}\.\~\`\*\+\^\%\$\#\@\!\=\\\/\,\<\>\?\'\"]/gim,
        ""
      );
      className = className.replace(/script|container|wrapper/gim, "REDACTED");
      if (/^[0-9]/gim.test(className)) {
        // console.log('ClassName starts with a number!');
        className = className.slice(1);
      }
      className = className[0].toLowerCase() + className.slice(1);
      return className;
    }
    return "";
  };

  //Handle Updating Chronicle
  const onSubmit = (formData) => {
    setLoading(true);
    // console.log('Chronicle Settings: ', formData);

    const { options } = formData;
    // let customClass = [];

    // if (options && options.customClass && options.customClass.length > 0) {

    // }
    const updatedChronicle = {
      title: formData.title,
      description: formData.description,
      private: formData.private,
      subGroups: formData.subGroups,
      defaultCalendar: formData.calendar,
      defaultMap: formData.map && formData.map.length > 0 ? formData.map : null,
      defaultCover:
        formData.cover && formData.cover.length > 0 ? formData.cover : null,
      startDate: formData.startDate,
      defaultUnit: formData.granularity,
      unitDisplayCount: formData.unitCount,
      options: {
        autoOpenSidebar: options.autoOpenSidebar,
        enableChronicleSidebar: options.enableChronicleSidebar,
        sendViewersToWorld: options.sendViewersToWorld,
        sendAuthorsToDashboard: options.sendAuthorsToDashboard,
        customClass: options.customClass,
      },
    };

    console.log("Chronicle Update: ", updatedChronicle);
    // return;

    dispatch(
      ChronicleActions.saveChronicle(chronicle._id, updatedChronicle)
    ).then((result) => {
      setLoading(false);
      // console.log('result', result);
      closeModal();
    });
  };

  //Handle Closing form/modal
  const handleCancel = (ev) => {
    setLoading(false);
    closeModal();
  };

  //Handle Image Selection: Cover
  const handleCoverChange = (onChange, newValue) => {
    if (typeof newValue === "string") {
      onChange(newValue);
    } else {
      if (newValue._id) {
        onChange(newValue._id);
      }
    }
  };

  //Handle Modal Change
  const handleModalChange = () => {
    //We need to get the current form state, store it, then restore upon re-render
    const formData = {};
    const fields = Object.keys(defaultValues);
    for (let field of fields) {
      formData[field] = getValues(field);
    }
    const prevFormState = { data: formData, defaultTab: currentMainTab };
    // console.log(prevFormState);
    return prevFormState;
  };

  //Set this components Main tab
  const handleTabChange = (tab) => {
    setCurrentMainTab(tab);
  };

  //Grab Latest Subscriber Groups
  useEffect(() => {
    if (world && world.waID) {
      dispatch(WorldActions.getSubGroups(world.waID));
    }
  }, []);

  const { isValidating, isSubmitting, dirtyFields, touched } = formState;

  const handleKeyClose = (ev) => {
    // console.log("Key up: ", ev);
    if (ev.key && ev.key.toLowerCase() === "escape") {
      console.log("Form State: ", { ...formState });
      if (!isValidating && !isSubmitting) {
        if (
          // formState.dirtyFields &&
          Object.keys(dirtyFields).length === 0 &&
          // formState.touched &&
          Object.keys(touched).length === 0
        ) {
          // console.log("dirty fields!");
          handleCancel();
        }
      }
    }
  };

  useEventListener("keyup", handleKeyClose);

  //Handle Auto-restore of FormState after Modal Change
  // useEffect(() => {
  //   console.log('Attempting to restore last formstate', prevForm);
  //   if (prevForm && prevForm.data) {
  //     reset(
  //       { ...prevForm.data },
  //       {
  //         errors: true, //True: Errors will not reset
  //         dirtyFields: true, //False: Dirty Fields should reset to supplied values (TEST)
  //         isDirty: true, //True: The form is considered dirty
  //         touched: true,
  //       }
  //     );
  //   }
  // }, [prevForm]);

  const tooltipID = "chronicleSettingsFormTooltip";
  const unitName =
    (defaultCalendar && defaultCalendar.time[watch("granularity")]?.plural) ||
    "Years";

  return (
    <div className="chronicle-settings-form" ref={componentRef}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="chronos-form chronos-modal-form"
      >
        <Tabs
          tabs={["General", "Display", "Privacy", "Time & Date"]}
          defaultTab={prevForm && prevForm.defaultTab ? prevForm.defaultTab : 0}
          onTabChange={handleTabChange}
        >
          {/* General Settings */}
          <div className="chronos-tab-pane">
            {/* <h4 className="form-section-title">Basic</h4> */}
            {/* Chronicle Title */}
            <div className="form-group">
              <label htmlFor="title" className="chronos-form-title">
                Title
              </label>
              <Controller
                control={control}
                name="title"
                id="title"
                as="input"
                placeholder="Chronicle Title"
                className="form-control"
                rules={{
                  required: {
                    value: true,
                    message: "You need to specify a name for the Chronicle.",
                  },
                  minLength: {
                    value: 3,
                    message:
                      "The Chronicle's name must be at least 3 characters.",
                  },
                  maxLength: {
                    value: 255,
                    message:
                      "The supplied title for the Chronicle is too long.",
                  },
                  validate: {
                    isValidLength: (value) =>
                      (value &&
                        value.length >= 3 &&
                        value.replaceAll(" ", "").length >= 3) ||
                      "The title for this Chronicle must be 3 characters, not counting spaces.",
                  },
                }}
              />
              <ErrorMessage errors={errors} name="title" />
            </div>

            {/* Chronicle Description */}
            <div className="form-group">
              <label htmlFor="description" className="chronos-form-title">
                Description &nbsp;
                <Icon
                  icon={tooltipIcon}
                  data-tip="A summary of the events of this Chronicle"
                  data-for={tooltipID}
                />
              </label>
              <Controller
                control={control}
                name="description"
                id="description"
                placeholder="General information regarding the subject(s) of this Chronicle"
                as="textarea"
                className="form-control"
                rows={5}
              />
            </div>

            {/* Privacy Settings: State */}
            {/* <h4 className="form-section-title">Privacy</h4> */}
          </div>
          {/* Chronicle Display Pane */}
          <div className="chronos-tab-pane">
            {/* <h4 className="form-section-title">Default Presentation</h4> */}

            {/* Default Cover */}
            <div className="form-group">
              {/* <label className="chronos-form-title">
                Default Cover Image &nbsp;
                <Icon
                  icon="far fa-info-circle"
                  data-tip={`This Image is used as a placeholder in the event there is no specified image for Events, Locations, or Maps.`}
                  data-place="top"
                  data-for={tooltipID}
                />
              </label> */}
              <Controller
                control={control}
                name="cover"
                render={({ onChange, value }) => (
                  <ImagePicker
                    value={value}
                    filters={["cover", "image"]}
                    labelText="Default Cover Image"
                    tooltipID={tooltipID}
                    tooltipIcon={tooltipIcon}
                    showTooltip
                    tooltipText={`This Image is used as a placeholder in the event there is no specified image for Events, Locations, or Maps.`}
                    onChange={(val) => handleCoverChange(onChange, val)}
                    allowNew
                    preNew={handleModalChange}
                  />
                )}
              />
            </div>

            {/* Default Map */}
            <div className="form-group">
              {/* <label className="chronos-form-title">
                Default Map &nbsp;
                <Icon
                  icon="far fa-info-circle"
                  data-tip={`This is the Map the Chronicle should display immediately when loaded. If this is not set, then the Default Cover Image (above) will be shown instead.`}
                  data-place="top"
                  data-for={tooltipID}
                />
              </label> */}
              <Controller
                control={control}
                name="map"
                render={({ value, onChange }) => (
                  <MapPicker
                    value={value}
                    onChange={onChange}
                    labelText="Default Map"
                    tooltipID={tooltipID}
                    tooltipIcon={tooltipIcon}
                    tooltipText={`This is the Map the Chronicle should display immediately when loaded. If this is not set, then the Default Cover Image (above) will be shown instead.`}
                    showTooltip
                    allowNew
                    preNew={handleModalChange}
                  />
                )}
              />
            </div>
            {/* <h4 className="form-section-title">Display Options</h4> */}

            <div className="form-group">
              {/* Open Chronicle Sidebar */}
              {/* <label className="chronos-form-title chronos-custom-form-label">
                <span className="">
                  <Icon
                    icon="fal fa-info-circle fa-fw"
                    data-tip="Checking this will automatically open the Chronicle's Sidebar when the Chronicle loads. This will only apply to visitors, not Authors."
                    data-place="top"
                    data-for="chronicleSettingsFormTooltip"
                  />
                  &nbsp;Open Sidebar Automatically?
                </span>
                <Controller
                  control={control}
                  name="options.autoOpenSidebar"
                  render={({ value, onChange }) => (
                    <Checkbox value={value} onChange={onChange} custom />
                  )}
                />
              </label> */}
              {/* Open Chronicle Sidebar */}
              {/* <label className="chronos-form-title chronos-custom-form-label">
                <span>
                  <Icon
                    icon="fal fa-info-circle fa-fw"
                    data-tip="Checking this will open the Chronicle's Sidebar (description) when the Chronicle Title (Black) is clicked."
                    data-place="top"
                    data-for="chronicleSettingsFormTooltip"
                  />
                  &nbsp;Open Sidebar when Chronicle Title is clicked?
                </span>
                <Controller
                  control={control}
                  name="options.enableChronicleSidebar"
                  render={({ value, onChange }) => (
                    <Checkbox value={value} onChange={onChange} custom />
                  )}
                />
              </label> */}
              {/* Navigate Viewers to World */}
              {/* <label className="chronos-form-title chronos-custom-form-label">
                <span>
                  <Icon
                    icon="fal fa-info-circle fa-fw"
                    data-tip="If this is checked, Viewers can quickly navigate to your world by clicking the Wold Title (Red) in the main window."
                    data-place="top"
                    data-for="chronicleSettingsFormTooltip"
                  />
                  &nbsp;Enable World Title Navigation (Viewers)?
                </span>
                <Controller
                  control={control}
                  name="options.sendViewersToWorld"
                  render={({ value, onChange }) => (
                    <Checkbox value={value} onChange={onChange} custom />
                  )}
                />
              </label> */}
              {/* Navigate Viewers to World */}
              {/* <label className="chronos-form-title chronos-custom-form-label">
                <span>
                  <Icon
                    icon="fal fa-info-circle fa-fw"
                    data-tip="If this is checked, Authors can quickly navigate to your world dashboard by clicking the Wold Title (Red) in the main window."
                    data-place="top"
                    data-for="chronicleSettingsFormTooltip"
                  />
                  &nbsp;Enable World Title Navigation (Authors)?
                </span>
                <Controller
                  control={control}
                  name="options.sendAuthorsToDashboard"
                  render={({ value, onChange }) => (
                    <Checkbox value={value} onChange={onChange} custom />
                  )}
                />
              </label> */}
            </div>
            <div className="form-group">
              <label
                htmlFor="options.customClass"
                className="chronos-form-title"
              >
                Custom CSS Class&nbsp;
                <Icon
                  icon={tooltipIcon}
                  data-tip="Adds a CSS class to the main Chronicle component, allowing you to target Chronicle elements using CSS. Note: You must have access to CSS to be able to benefit from this."
                  data-place="top"
                  data-for="chronicleSettingsFormTooltip"
                />
              </label>
              <Controller
                control={control}
                name="options.customClass"
                render={({ value, onChange }) => {
                  const handleChange = (ev) => {
                    if (onChange) {
                      const { value } = ev.target;
                      onChange(formatClassname(value));
                    }
                  };
                  return (
                    <input
                      value={value}
                      onChange={handleChange}
                      placeholder={formatClassname(chronicle.title)}
                      className="form-control"
                      id="options.customClass"
                    />
                  );
                }}
              />
            </div>
          </div>
          {/* Chronicle Privacy Settings Pane */}
          <div className="chronos-tab-pane">
            <div className="form-group">
              <label htmlFor="private" className="chronos-form-title">
                Privacy &nbsp;
                <Icon
                  icon={tooltipIcon}
                  data-tip="Set this Chronicle to either Public or Private"
                  data-for={tooltipID}
                />
              </label>
              <br />
              <Controller
                control={control}
                name="private"
                defaultValue={false}
                render={({ onChange, value }) => (
                  <Toggle
                    id="private"
                    name="private"
                    checked={value}
                    onChange={onChange}
                    onText="Private"
                    offText="Public"
                    onColor="#dc3545"
                    offColor="#007bff"
                  />
                )}
              />
            </div>
            {watch("private") === true && (
              <div className="form-group">
                <label htmlFor="subGroups" className="chronos-form-title">
                  Subscriber Groups Access &nbsp;
                  <Icon
                    icon={tooltipIcon}
                    data-tip="Choose which groups to allow access to this Chronicle"
                    data-for={tooltipID}
                  />
                </label>
                <Controller
                  control={control}
                  name="subGroups"
                  render={({ onChange, value }) => (
                    <SubGroupPicker value={value} onChange={onChange} />
                  )}
                />
              </div>
            )}
          </div>
          {/* Chronicle Time & Date Pane */}
          <div className="chronos-tab-pane">
            {/* Default Calendar */}
            {/* <div className="form-group">
              <label htmlFor="calendar" className="chronos-form-title">
                Default Calendar &nbsp;
                <Icon
                  icon="far fa-info-circle"
                  data-tip="Coming Soon! Select the Chronicle's default Calendar."
                  data-for={tooltipID}
                />
              </label>
              <Controller
                control={control}
                name="calendar"
                id="calendar"
                render={({ value, onChange }) => (
                  <CalendarSelect
                    // allowTemplates
                    value={value}
                    onChange={onChange}
                    disabled={true}
                    placeholder="Currently Unavailable (Under Construction)"
                  />
                )}
              />
            </div> */}
            {/* Default Granularity */}
            <div className="form-group">
              <label htmlFor="granularity" className="chronos-form-title">
                Default Unit of Time &nbsp;
                <Icon
                  icon={tooltipIcon}
                  data-tip={`This is the default unit of time that the Chronicle uses for calculating timelines when it loads.`}
                  data-place="top"
                  data-for="chronicleSettingsFormTooltip"
                />
              </label>
              <Controller
                control={control}
                name="granularity"
                id="granularity"
                render={({ value, onChange }) => {
                  let defaultValue = granularityOptions.find(
                    (o) => o.value === value
                  );
                  if (!defaultValue) {
                    defaultValue = { name: "Years", value: "year" };
                  }
                  const handleChange = (val) => {
                    console.log("Granularity Set to:", val);
                    onChange(val[0].value);
                  };
                  return (
                    <Select
                      options={granularityOptions}
                      values={[defaultValue]}
                      onChange={handleChange}
                      dropdownGap={0}
                      labelField="name"
                      valueField="value"
                      className="chronos-select"
                    />
                  );
                }}
              />
            </div>
            {/* Default Granularity */}
            <div className="form-group">
              <label htmlFor="unitCount" className="chronos-form-title">
                Number of {unitName} to display on timeline&nbsp;
                <Icon
                  icon={tooltipIcon}
                  data-tip={`This setting controls how many ${unitName} to display at once`}
                  data-place="top"
                  data-for="chronicleSettingsFormTooltip"
                />
              </label>
              <br />
              <Controller
                control={control}
                name="unitCount"
                id="unitCount"
                render={({ value, onChange }) => {
                  const handleChange = (values) => {
                    // console.log("Received: ", values);
                    const { value } = values;
                    onChange(value);
                    trigger("unitCount");
                  };
                  console.log("Unit Count", value);
                  return (
                    <NumberInput
                      thousandSeparator={true}
                      defaultValue={value}
                      values={value}
                      onValueChange={handleChange}
                      className="form-control"
                      style={{ width: "8em", display: "inline-block" }}
                      min={1}
                      max={1000}
                    />
                  );
                }}
                type="customnumberinput"
                rules={{
                  required: {
                    value: true,
                    message:
                      "You must specify the amount of time units to display.",
                  },
                  validate: {
                    isNumeric: (value) => {
                      console.log("--Value: ", parseInt(value, 10));
                      return (
                        !isNaN(parseInt(value, 10)) ||
                        "You must specify a number"
                      );
                    },
                    minimum: (value) =>
                      parseInt(value, 10) >= 1 ||
                      "You must display at least 1 time unit.",
                    maximum: (value) =>
                      parseInt(value, 10) <= 1000 ||
                      "You must display 1,000 or fewer time units",
                  },
                  // min: {
                  //   value: 1,
                  //   message: "You must display at least 1 time unit.",
                  // },
                  // max: {
                  //   value: 1000,
                  //   message: "You must display 1,000 or fewer time units",
                  // },
                }}
              />
              <br />
              <ErrorMessage errors={errors} name="unitCount" />
            </div>
            {/* Default Start Date */}
            <div className="form-group">
              <label htmlFor="startDate" className="chronos-form-title">
                Chronicle Start Date &nbsp;
                <Icon
                  icon={tooltipIcon}
                  data-tip={`This is the default loading date for the Chronicle.`}
                  data-place="top"
                  data-for="chronicleSettingsFormTooltip"
                />
              </label>
              <Controller
                control={control}
                name="startDate"
                id="startDate"
                render={({ value, onChange }) => {
                  // console.log('Default Calendar', defaultCalendar);
                  // console.log('Start Date', chronicle.startDate);
                  return (
                    <CalendarDatePicker
                      calendar={defaultCalendar}
                      onChange={onChange}
                      value={value}
                    />
                  );
                }}
                rules={{
                  required: {
                    value: true,
                    message:
                      "You must specify the starting Date/Time for the Chronicle.",
                  },
                }}
              />
              <ErrorMessage errors={errors} name="startDate" />
            </div>
          </div>
        </Tabs>
        <div className="form-controls">
          {/* <Button color="secondary" onClick={handleCancel}>
            <Icon icon="far fa-ban fa-fw" />
            &nbsp;Cancel
          </Button> */}
          &nbsp;
          <Button color="primary" type="submit">
            <Icon icon="fas fa-save fa-fw" />
            &nbsp;Save
          </Button>
        </div>
        <DevTool control={control} />
        <ReactTooltip effect="solid" id={tooltipID} />
        <PaneLoader text="Settings" isLoading={isLoading} />
      </form>
    </div>
  );
}
